/**
 * XY Graph Preprocessor
 *
 * This module handles the conversion of XY graph data from JSON format
 * to responsive SVG charts. It provides a clean, maintainable interface
 * for rendering mathematical plots and graphs.
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface XYGraphPlot {
  xPts: number[];
  yPts: number[];
  lineStyle?: string;
  color?: string;
  visible?: boolean;
  hint?: string;
  plotType?: string;
}

interface XYGraphAxis {
  maximum: number;
  minimum?: number;
  tickSpacing?: number;
  title?: string;
  color?: string;
  titleFontFamily?: string;
  titleFontSize?: string;
  labelFontFamily?: string;
  labelFontSize?: string;
  tickStyle?: string;
  customTicks?: unknown[];
  valueFormat?: string;
  showGrid?: boolean;
  orientation?: string;
}

interface XYGraphMargins {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

interface XYGraphData {
  type?: string;
  id: string;
  name: string;
  visible?: boolean;
  width: number;
  height: number;
  titleFont?: string;
  titleFontSize?: number;
  plotMargins: XYGraphMargins;
  showOutline?: boolean;
  outlineColor?: string;
  graphTitle?: string;
  titlePosition?: string;
  size?: number;
  titleColor?: string;
  graphStyle?: string;
  altText?: string;
  hidePointLines?: boolean;
  hidePointValues?: boolean;
  plots: XYGraphPlot[];
  axisX?: XYGraphAxis;
  axisY?: XYGraphAxis;
  labels?: unknown[];
  regions?: unknown[];
  shapes?: unknown[];
  tabAlign?: string;
}

// Validated graph data with guaranteed axis data
interface XYGraphDataWithAxes extends Omit<XYGraphData, 'axisX' | 'axisY'> {
  axisX: XYGraphAxis;
  axisY: XYGraphAxis;
}

// ============================================================================
// CONSTANTS AND CONFIGURATION
// ============================================================================

const CHART_CONFIG = {
  // Fixed dimensions for responsive behavior
  WIDTH: 600,
  HEIGHT: 400,
  MARGINS: { left: 50, top: 30, right: 30, bottom: 50 },

  // Styling constants
  COLORS: {
    AXIS: '#333',
    GRID: '#e0e0e0',
    TEXT: '#333',
    PLOT: '#000000',
    OUTLINE: 'black',
  },

  // Typography
  FONTS: {
    FAMILY: 'var(--inter)',
    LABEL_SIZE: '0.875rem',
    TITLE_SIZE: '1rem',
  },

  // Stroke widths
  STROKES: {
    AXIS: 2,
    GRID: 1,
    PLOT: 2,
    OUTLINE: 1,
  },

  // Spacing and positioning
  TICK_LENGTH: 5,
  LABEL_OFFSET: 18,
  TITLE_OFFSET: 10,
} as const;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Formats a numeric value for display on the graph
 * @param value - The numeric value to format
 * @param format - Optional format string (e.g., '#.--' for integers)
 * @returns Formatted string representation
 */
function formatXYGraphValue(value: number, format?: string): string {
  if (format === '#.--') {
    return Math.round(value).toString();
  }

  if (Number.isInteger(value)) {
    return value.toString();
  }

  // Round to 2 decimal places for display
  return (Math.round(value * 100) / 100).toString();
}

/**
 * Converts graph coordinate to SVG X position
 * @param value - Graph coordinate value
 * @param axis - Axis configuration
 * @param chartWidth - Width of chart area
 * @param marginLeft - Left margin
 * @returns SVG X coordinate
 */
function graphToSVGX(
  value: number,
  axis: XYGraphAxis,
  chartWidth: number,
  marginLeft: number
): number {
  const minimum = axis.minimum ?? 0;
  const range = axis.maximum - minimum;
  const ratio = (value - minimum) / range;
  return marginLeft + ratio * chartWidth;
}

/**
 * Converts graph coordinate to SVG Y position
 * @param value - Graph coordinate value
 * @param axis - Axis configuration
 * @param chartHeight - Height of chart area
 * @param marginTop - Top margin
 * @returns SVG Y coordinate
 */
function graphToSVGY(
  value: number,
  axis: XYGraphAxis,
  chartHeight: number,
  marginTop: number
): number {
  const minimum = axis.minimum ?? 0;
  const range = axis.maximum - minimum;
  const ratio = (value - minimum) / range;
  // Flip Y coordinate (SVG Y increases downward)
  return marginTop + chartHeight - ratio * chartHeight;
}

/**
 * Escapes HTML entities in text content
 * @param text - Text to escape
 * @returns Escaped text safe for SVG
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Cleans alt text by removing special formatting characters
 * @param altText - Raw alt text
 * @returns Cleaned alt text
 */
function cleanXYGraphAltText(altText?: string): string {
  if (!altText) return '';

  return altText
    .replace(/☻[01]☺/g, '')
    .replace(/☻0☺@div\{[^}]+\}☻0☺/g, '')
    .replace(/[☻☺]/g, '')
    .trim();
}

// ============================================================================
// SVG GENERATION FUNCTIONS
// ============================================================================

/**
 * Generates CSS styles for the SVG
 * @returns CSS style string
 */
function generateSVGStyles(): string {
  return `
    .xygraph-axis { 
      stroke: ${CHART_CONFIG.COLORS.AXIS}; 
      stroke-width: ${CHART_CONFIG.STROKES.AXIS}; 
    }
    .xygraph-grid { 
      stroke: ${CHART_CONFIG.COLORS.GRID}; 
      stroke-width: ${CHART_CONFIG.STROKES.GRID}; 
      opacity: 0.6; 
    }
    .xygraph-label { 
      font-family: ${CHART_CONFIG.FONTS.FAMILY}; 
      font-size: ${CHART_CONFIG.FONTS.LABEL_SIZE}; 
      fill: ${CHART_CONFIG.COLORS.TEXT}; 
    }
    .xygraph-title { 
      font-family: ${CHART_CONFIG.FONTS.FAMILY}; 
      font-size: ${CHART_CONFIG.FONTS.TITLE_SIZE}; 
      fill: ${CHART_CONFIG.COLORS.TEXT}; 
      font-weight: bold; 
    }
    .xygraph-outline { 
      stroke: ${CHART_CONFIG.COLORS.OUTLINE}; 
      stroke-width: ${CHART_CONFIG.STROKES.OUTLINE}; 
      fill: none; 
    }
  `
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Generates grid lines for the chart
 * @param graphData - Graph data configuration (with guaranteed axis data)
 * @param chartDimensions - Chart dimensions and margins
 * @returns SVG grid elements
 */
function generateGridLines(
  graphData: XYGraphDataWithAxes,
  chartDimensions: {
    left: number;
    top: number;
    width: number;
    height: number;
  }
): string {
  const { left, top, width, height } = chartDimensions;
  let gridHtml = '';

  // X-axis grid lines (vertical)
  if (graphData.axisX.tickSpacing && graphData.axisX.showGrid) {
    const xMin = graphData.axisX.minimum ?? 0;
    for (let x = xMin; x <= graphData.axisX.maximum; x += graphData.axisX.tickSpacing) {
      const svgX = graphToSVGX(x, graphData.axisX, width, left);
      gridHtml += `<line x1="${svgX}" y1="${top}" x2="${svgX}" y2="${
        top + height
      }" class="xygraph-grid" />`;
    }
  }

  // Y-axis grid lines (horizontal)
  if (graphData.axisY.tickSpacing && graphData.axisY.showGrid) {
    const yMin = graphData.axisY.minimum ?? 0;
    for (let y = yMin; y <= graphData.axisY.maximum; y += graphData.axisY.tickSpacing) {
      const svgY = graphToSVGY(y, graphData.axisY, height, top);
      gridHtml += `<line x1="${left}" y1="${svgY}" x2="${
        left + width
      }" y2="${svgY}" class="xygraph-grid" />`;
    }
  }

  return gridHtml;
}

/**
 * Generates axis lines, ticks, and labels
 * @param graphData - Graph data configuration (with guaranteed axis data)
 * @param chartDimensions - Chart dimensions and margins
 * @returns SVG axis elements
 */
function generateAxes(
  graphData: XYGraphDataWithAxes,
  chartDimensions: {
    left: number;
    top: number;
    width: number;
    height: number;
  }
): string {
  const { left, top, width, height } = chartDimensions;
  let axesHtml = '';

  // Main axis lines
  const xAxisY = top + height;
  axesHtml += `<line x1="${left}" y1="${top}" x2="${left}" y2="${xAxisY}" class="xygraph-axis" />`; // Y-axis
  axesHtml += `<line x1="${left}" y1="${xAxisY}" x2="${
    left + width
  }" y2="${xAxisY}" class="xygraph-axis" />`; // X-axis

  // X-axis ticks and labels
  if (graphData.axisX.tickSpacing) {
    const xMin = graphData.axisX.minimum ?? 0;
    for (let x = xMin; x <= graphData.axisX.maximum; x += graphData.axisX.tickSpacing) {
      const svgX = graphToSVGX(x, graphData.axisX, width, left);

      // Tick mark
      axesHtml += `<line x1="${svgX}" y1="${xAxisY}" x2="${svgX}" y2="${
        xAxisY + CHART_CONFIG.TICK_LENGTH
      }" stroke="${CHART_CONFIG.COLORS.AXIS}" stroke-width="1" />`;

      // Label
      const label = escapeHtml(formatXYGraphValue(x, graphData.axisX.valueFormat));
      axesHtml += `<text x="${svgX}" y="${
        xAxisY + CHART_CONFIG.LABEL_OFFSET
      }" text-anchor="middle" class="xygraph-label">${label}</text>`;
    }
  }

  // Y-axis ticks and labels
  if (graphData.axisY.tickSpacing) {
    const yMin = graphData.axisY.minimum ?? 0;
    for (let y = yMin; y <= graphData.axisY.maximum; y += graphData.axisY.tickSpacing) {
      const svgY = graphToSVGY(y, graphData.axisY, height, top);

      // Tick mark
      axesHtml += `<line x1="${
        left - CHART_CONFIG.TICK_LENGTH
      }" y1="${svgY}" x2="${left}" y2="${svgY}" stroke="${
        CHART_CONFIG.COLORS.AXIS
      }" stroke-width="1" />`;

      // Label
      const label = escapeHtml(formatXYGraphValue(y, graphData.axisY.valueFormat));
      axesHtml += `<text x="${left - 10}" y="${
        svgY + 4
      }" text-anchor="end" class="xygraph-label">${label}</text>`;
    }
  }

  return axesHtml;
}

/**
 * Generates plot paths for the chart
 * @param graphData - Graph data configuration (with guaranteed axis data)
 * @param chartDimensions - Chart dimensions and margins
 * @returns SVG plot elements
 */
function generatePlots(
  graphData: XYGraphDataWithAxes,
  chartDimensions: {
    left: number;
    top: number;
    width: number;
    height: number;
  }
): string {
  const { left, top, width, height } = chartDimensions;
  let plotsHtml = '';

  graphData?.plots?.forEach((plot) => {
    if (
      plot.visible === false ||
      !plot.xPts?.length ||
      !plot.yPts?.length ||
      plot.xPts.length !== plot.yPts.length
    ) {
      return;
    }

    const color = plot.color || CHART_CONFIG.COLORS.PLOT;
    const lineStyle = plot.lineStyle || 'Solid';

    // Generate path data
    const pathPoints = plot.xPts.map((x, i) => {
      const svgX = graphToSVGX(x, graphData.axisX, width, left);
      const svgY = graphToSVGY(plot.yPts[i], graphData.axisY, height, top);
      return `${svgX} ${svgY}`;
    });

    if (pathPoints.length > 1) {
      const pathData = `M ${pathPoints.join(' L ')}`;
      const strokeDashArray =
        lineStyle === 'Dashed'
          ? ' stroke-dasharray="5,5"'
          : lineStyle === 'Dotted'
          ? ' stroke-dasharray="2,2"'
          : '';

      plotsHtml += `<path d="${pathData}" stroke="${color}" stroke-width="${CHART_CONFIG.STROKES.PLOT}" fill="none"${strokeDashArray} />`;
    }
  });

  return plotsHtml;
}

/**
 * Generates the main SVG chart
 * @param graphData - Graph data configuration (with guaranteed axis data)
 * @returns Complete SVG string
 */
function generateXYGraphSVG(graphData: XYGraphDataWithAxes): string {
  const { WIDTH, HEIGHT, MARGINS } = CHART_CONFIG;

  const chartDimensions = {
    left: MARGINS.left,
    top: MARGINS.top,
    width: WIDTH - MARGINS.left - MARGINS.right,
    height: HEIGHT - MARGINS.top - MARGINS.bottom,
  };

  const svgContent = [
    `<svg width="100%" height="100%" viewBox="0 0 ${WIDTH} ${HEIGHT}" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" class="xygraph-svg">`,
    `<defs><style>${generateSVGStyles()}</style></defs>`,
    `<rect x="0" y="0" width="${WIDTH}" height="${HEIGHT}" class="xygraph-outline" />`,
    generateGridLines(graphData, chartDimensions),
    generateAxes(graphData, chartDimensions),
    generatePlots(graphData, chartDimensions),
    graphData.graphTitle
      ? `<text x="${WIDTH / 2}" y="${
          HEIGHT - CHART_CONFIG.TITLE_OFFSET
        }" text-anchor="middle" class="xygraph-title">${escapeHtml(graphData.graphTitle)}</text>`
      : '',
    '</svg>',
  ].join('');

  return svgContent;
}

// ============================================================================
// DATA PARSING AND VALIDATION
// ============================================================================

/**
 * Auto-calculates axis ranges from plot data
 * @param plots - Array of plot data
 * @returns Calculated axis configuration with padding
 */
function calculateAxisFromPlots(plots: XYGraphPlot[]): { axisX: XYGraphAxis; axisY: XYGraphAxis } {
  let xMin = Infinity;
  let xMax = -Infinity;
  let yMin = Infinity;
  let yMax = -Infinity;

  // Find min/max across all visible plots
  plots.forEach((plot) => {
    if (plot.visible !== false && plot.xPts?.length && plot.yPts?.length) {
      plot.xPts.forEach((x) => {
        xMin = Math.min(xMin, x);
        xMax = Math.max(xMax, x);
      });
      plot.yPts.forEach((y) => {
        yMin = Math.min(yMin, y);
        yMax = Math.max(yMax, y);
      });
    }
  });

  // If no valid data found, use defaults
  if (!isFinite(xMin) || !isFinite(xMax)) {
    xMin = 0;
    xMax = 10;
  }
  if (!isFinite(yMin) || !isFinite(yMax)) {
    yMin = 0;
    yMax = 10;
  }

  // Add padding (10% on each side)
  const xRange = xMax - xMin;
  const yRange = yMax - yMin;
  const paddingX = xRange * 0.1 || 1;
  const paddingY = yRange * 0.1 || 1;

  return {
    axisX: {
      minimum: xMin - paddingX,
      maximum: xMax + paddingX,
    },
    axisY: {
      minimum: yMin - paddingY,
      maximum: yMax + paddingY,
    },
  };
}

/**
 * Ensures axis data exists, auto-calculating from plots if missing
 * @param graphData - Graph data that may be missing axis data
 * @returns Graph data with guaranteed axis data
 */
function ensureAxisData(graphData: XYGraphData): XYGraphDataWithAxes {
  // If axis data is missing or incomplete, calculate from plots
  if (
    !graphData.axisX ||
    !graphData.axisY ||
    typeof graphData.axisX.maximum !== 'number' ||
    typeof graphData.axisY.maximum !== 'number'
  ) {
    if (!graphData.plots || !Array.isArray(graphData.plots) || graphData.plots.length === 0) {
      // No plots to calculate from, use defaults
      return {
        ...graphData,
        axisX: graphData.axisX || { minimum: 0, maximum: 10 },
        axisY: graphData.axisY || { minimum: 0, maximum: 10 },
      };
    }

    const calculatedAxes = calculateAxisFromPlots(graphData.plots);

    // Merge with existing axis data if any (preserve tickSpacing, showGrid, etc.)
    return {
      ...graphData,
      axisX: {
        ...calculatedAxes.axisX,
        ...graphData.axisX,
        minimum: graphData.axisX?.minimum ?? calculatedAxes.axisX.minimum,
        maximum: graphData.axisX?.maximum ?? calculatedAxes.axisX.maximum,
      },
      axisY: {
        ...calculatedAxes.axisY,
        ...graphData.axisY,
        minimum: graphData.axisY?.minimum ?? calculatedAxes.axisY.minimum,
        maximum: graphData.axisY?.maximum ?? calculatedAxes.axisY.maximum,
      },
    };
  }

  // If we reach here, axis data exists but ensure it's complete
  return {
    ...graphData,
    axisX: graphData.axisX || { minimum: 0, maximum: 10 },
    axisY: graphData.axisY || { minimum: 0, maximum: 10 },
  };
}

/**
 * Extracts XY graph data from parsed JSON structure
 * @param parsedData - Parsed JSON data
 * @returns XY graph data or null if not found
 */
function extractXYGraphData(parsedData: unknown): XYGraphData | null {
  if (parsedData && typeof parsedData === 'object') {
    const data = parsedData as Record<string, unknown>;

    // Direct xygraph object
    if (data.type === 'xygraph') {
      return data as unknown as XYGraphData;
    }

    // Nested structure with choices
    if (data.choices && Array.isArray(data.choices)) {
      for (const choice of data.choices) {
        if (choice && typeof choice === 'object' && 'content' in choice) {
          const content = (choice as Record<string, unknown>).content;
          if (Array.isArray(content)) {
            for (const contentItem of content) {
              if (contentItem && typeof contentItem === 'object' && 'content' in contentItem) {
                const nestedContent = (contentItem as Record<string, unknown>).content;
                if (Array.isArray(nestedContent)) {
                  for (const item of nestedContent) {
                    if (
                      item &&
                      typeof item === 'object' &&
                      (item as Record<string, unknown>).type === 'xygraph'
                    ) {
                      return item as unknown as XYGraphData;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  return null;
}

/**
 * Validates XY graph data structure
 * @param graphData - Graph data to validate
 * @returns Validation result with error message if invalid
 */
function validateXYGraphData(graphData: XYGraphData): { isValid: boolean; error?: string } {
  // Plots are required (we need data to render)
  if (!graphData.plots || !Array.isArray(graphData.plots)) {
    return { isValid: false, error: 'Invalid XY graph data structure - missing plots array' };
  }

  // Check if we have at least one valid plot with data
  const hasValidPlot = graphData.plots.some(
    (plot) =>
      plot.visible !== false &&
      plot.xPts?.length &&
      plot.yPts?.length &&
      plot.xPts.length === plot.yPts.length
  );

  if (!hasValidPlot) {
    return {
      isValid: false,
      error: 'Invalid XY graph data structure - no valid plots with data points',
    };
  }

  // Axis data will be auto-calculated if missing, so we don't require it here
  // But if provided, it must have valid maximum values
  if (graphData.axisX && typeof graphData.axisX.maximum !== 'number') {
    return {
      isValid: false,
      error: 'Invalid XY graph data structure - axisX.maximum must be a number',
    };
  }

  if (graphData.axisY && typeof graphData.axisY.maximum !== 'number') {
    return {
      isValid: false,
      error: 'Invalid XY graph data structure - axisY.maximum must be a number',
    };
  }

  return { isValid: true };
}

// ============================================================================
// MAIN EXPORT FUNCTION
// ============================================================================

/**
 * Main preprocessing function to find and replace XY graph data with SVG
 * @param content - Content string containing XY graph markers
 * @returns Processed content with SVG charts
 */
export function preprocessXYGraphContent(content: string): string {
  const xyGraphRegex = /%%XY%%(.*?)%%XY%%/gs;

  return content.replace(xyGraphRegex, (match: string, jsonData: string) => {
    try {
      const parsedData = JSON.parse(jsonData.trim());
      const graphData = extractXYGraphData(parsedData);

      if (!graphData) {
        console.error('Could not extract XY graph data from JSON structure');
        return '<div class="xygraph-error">Invalid XY graph data structure</div>';
      }

      const validation = validateXYGraphData(graphData);
      if (!validation.isValid) {
        console.error(validation.error);
        return `<div class="xygraph-error">${validation.error}</div>`;
      }

      // Ensure axis data exists (auto-calculate from plots if missing)
      const graphDataWithAxes = ensureAxisData(graphData);

      const svgHtml = generateXYGraphSVG(graphDataWithAxes);
      const altText = cleanXYGraphAltText(graphDataWithAxes.altText);
      const altTextHtml = altText
        ? `<div class="xygraph-alt-text sr-only">${escapeHtml(altText)}</div>`
        : '';

      const svgNode = String(svgHtml)
        .replace(/\n/g, '') // remove newlines
        .replace(/\s+/g, ' ') // collapse whitespace
        .replace(/>\s+</g, '><'); // remove gaps between tags
      // Properly encode for Base64
      const base64 = btoa(unescape(encodeURIComponent(svgNode)));

      return `<span class="xygraph-container"><img src="${base64}" alt="${altText}" about="image/svg" />${altTextHtml}</span>`;
    } catch (error) {
      console.error('Error parsing XY graph data:', error);
      return '<div class="xygraph-error">Error rendering XY graph</div>';
    }
  });
}
